import { useId, useState } from 'react';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import type { Area, PriorityGroup } from '../../types';

import { Table } from '@/shared/components/table';
import useControllableState from '@/shared/hooks/useControllableState';

interface PrecisionAreasListProps {
  areas: Record<PriorityGroup, Area[]>;
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  controlledPriorityGroup?: PriorityGroup;
  onPriorityGroupChange?: (priorityGroup: PriorityGroup) => void;
}

const columnHelper = createColumnHelper<Area>();

const baseColumns = [
  columnHelper.accessor('ranking', {
    header: () => (
      <Typography variant='body3' component='span'>
        Ranking
      </Typography>
    ),
    cell: (info) => (
      <Typography variant='subtitleNumbers' component='span'>
        {info.getValue()}
      </Typography>
    ),
    size: 70,
  }),

  columnHelper.accessor('areaName', {
    header: () => (
      <Typography variant='body3' component='span'>
        Area Name
      </Typography>
    ),
    cell: (info) => (
      <Typography
        variant='subtitleNumbers'
        component='span'
        whiteSpace='break-spaces'
      >
        {info.getValue()}
      </Typography>
    ),
    size: 300,
  }),

  columnHelper.accessor('populationSize', {
    header: () => (
      <Typography variant='body3' component='span'>
        Population Size
      </Typography>
    ),
    cell: (info) => (
      <Typography variant='subtitleNumbers' component='span'>
        {new Intl.NumberFormat('en-US', {
          maximumFractionDigits: 0,
          style: 'decimal',
        }).format(info.getValue())}
      </Typography>
    ),
  }),
];

const PrecisionAreasList = ({
  areas,
  isLoading = false,
  isFetching = false,
  isError = false,
  controlledPriorityGroup,
  onPriorityGroupChange,
}: PrecisionAreasListProps) => {
  const id = useId();
  const priorityGroupTextFieldId = `priority-group-${id}`;
  const [priorityGroup, setPriorityGroup] = useControllableState<PriorityGroup>(
    {
      defaultValue: 'L',
      value: controlledPriorityGroup,
      onChange: onPriorityGroupChange,
    }
  );
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const table = useReactTable<Area>({
    data: areas[priorityGroup],
    columns: baseColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    enableSorting: true,
    state: { pagination },
    onPaginationChange: (updater) => {
      const next =
        typeof updater === 'function'
          ? updater(table.getState().pagination)
          : updater;
      if (
        next.pageIndex === pagination.pageIndex &&
        next.pageSize === pagination.pageSize
      ) {
        return;
      }
      setPagination(next);
    },
  });

  return (
    <Paper
      elevation={0}
      sx={[
        (theme) => ({
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: theme.customShadows.sm,
        }),
        (theme) =>
          theme.applyStyles('dark', {
            border: 0,
            boxShadow: 'none',
          }),
      ]}
    >
      <Stack p={3} direction='row' alignItems='center'>
        <Box>
          <Typography variant='h6' component='h2'>
            List of Precision Area
          </Typography>
          <Typography variant='body2' color='text.secondary' pt={0.5}>
            Explore precision areas by priority group.
          </Typography>
        </Box>

        <Box alignSelf='flex-end' marginLeft='auto'>
          <TextField
            select
            id={priorityGroupTextFieldId}
            size='small'
            label='Priority Group'
            value={priorityGroup}
            onChange={(e) => {
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              setPriorityGroup(e.target.value as PriorityGroup);
            }}
            sx={{ width: 200 }}
          >
            <MenuItem value='L'>Low</MenuItem>
            <MenuItem value='M'>Medium</MenuItem>
            <MenuItem value='H'>High</MenuItem>
            <MenuItem value='VH'>Very High</MenuItem>
          </TextField>
        </Box>
      </Stack>

      <Divider />

      <Table
        table={table}
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        tableLabel='Precision Area Sizes Table'
        errorText='There was an error loading the data. Please try again.'
        emptyText='No data available.'
      />
    </Paper>
  );
};

export default PrecisionAreasList;
